import React, { FC } from 'react';

import { useTranslation } from '@crehana/i18n';
import { InverseButton } from '@crehana/ui';
import { useMe } from '@crehana/web';

import type { CourseCardProps } from '../../types';

import ClassicAddToCartBtn from './ClassicAddToCartBtn';
import TemporalCampaignButton from './TemporalCampaignButton';
import TemporalAccessButton from './TemporalAccessButton';
import Temporal24HoursButton from './Temporal24HoursButton';
import FreeCourseButton from './FreeCourseButton';
import useCourseMethods from './hooks/useCourseMethods';

const CustomButtons: FC<{
  renderCustomCallbackButtons: () => JSX.Element;
}> = ({ renderCustomCallbackButtons }) => {
  return renderCustomCallbackButtons();
};

const AddToCart: FC<Partial<CourseCardProps>> = props => {
  const {
    inShoppingCart,
    loadingCart,
    courseType,
    isEnrollment,
    originalId,
    pageType,
    enrollmentAccessPreviewUrl,
    enrollmentUrl,
    url,
    pageId,
    temporalAccessDurationLabel,
    renderCustomCallbackButtons,
    isUserSofeAndCourseIsOneMonthOld,
    source = '',
    sourceDetail,
    isDark,
    prices,
    removeCourseFromCart,
    addCourseToCart,
    urlToBuyNow,
    onClickBuyNow,
    product,
    title,
    beforeBuy,
  } = props;
  const { me: user } = useMe();
  const { t } = useTranslation();
  const { handleAddToCart, handleDirectBuyButton } = useCourseMethods({
    courseId: originalId,
    url,
    removeCourseFromCart,
    addCourseToCart,
    urlToBuyNow,
    onClickBuyNow,
    product,
    beforeBuy,
    title,
    inShoppingCart,
  });

  // Classroom continue button
  if (isEnrollment) {
    const classroomUrl = `/clases/v2/${originalId}/`;

    return (
      <InverseButton
        full
        className="mt-16 rounded-10"
        href={classroomUrl}
        // @ts-expect-error
        testId="watch-course"
        label={t('GO_TO_COURSE', {
          defaultValue: 'Ir al curso',
        })}
        isDark={isDark}
      />
    );
  }

  // Custom render
  if (renderCustomCallbackButtons) {
    return (
      <CustomButtons
        renderCustomCallbackButtons={renderCustomCallbackButtons}
      />
    );
  }

  if (pageType === 'COURSE TEMPORAL ACCESS') {
    return (
      <TemporalCampaignButton
        source={source}
        sourceDetail={sourceDetail}
        temporalAccessDurationLabel={temporalAccessDurationLabel}
        isDark={isDark}
        isUserSofeAndCourseIsOneMonthOld={isUserSofeAndCourseIsOneMonthOld}
        user={user}
        pageId={pageId}
        courseId={originalId}
      />
    );
  }

  if (courseType === 'temporal-access' || user?.canEnrollFreemium) {
    return (
      <Temporal24HoursButton
        isDark={isDark}
        url={url}
        temporalAccessDurationLabel={temporalAccessDurationLabel}
        isUserSofeAndCourseIsOneMonthOld={isUserSofeAndCourseIsOneMonthOld}
      />
    );
  }

  if (pageType === 'COURSE_ACCESS_PREVIEW' && courseType === 'freemium') {
    return (
      <TemporalAccessButton
        enrollmentAccessPreviewUrl={enrollmentAccessPreviewUrl}
        isDark={isDark}
        isUserSofeAndCourseIsOneMonthOld={isUserSofeAndCourseIsOneMonthOld}
      />
    );
  }

  if (courseType === 'free' && prices?.price === 0) {
    return (
      <FreeCourseButton
        isDark={isDark}
        course={{
          enrollmentUrl,
        }}
      />
    );
  }

  // Premium only course button
  if (courseType === 'membership')
    return (
      <InverseButton
        className="mt-16 rounded-10"
        full
        href={url}
        label="Acceso Premium"
        isDark={isDark}
      />
    );

  return (
    <ClassicAddToCartBtn
      isDark={isDark}
      loadingCart={loadingCart}
      inShoppingCart={inShoppingCart}
      handleDirectBuyButton={handleDirectBuyButton}
      handleAddToCart={handleAddToCart}
    />
  );
};

export default AddToCart;
